// src/components/resumo/components/PageNavigator.tsx
import React from 'react';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import './PageNavigator.css';

export interface ResumoPage {
    id: string;
    title: string;
    content: string;
    order: number;
}

interface PageNavigatorProps {
    pages: ResumoPage[];
    currentPageIndex: number;
    onPageChange: (index: number) => void;
    onAddPage: () => void;
    isEditing?: boolean;
    darkMode?: boolean;
    maxVisibleIndex?: number;
}

const PageNavigator: React.FC<PageNavigatorProps> = ({
    pages,
    currentPageIndex,
    onPageChange,
    onAddPage,
    isEditing = false,
    darkMode = false,
    maxVisibleIndex,
}) => {
    const hasPrevious = currentPageIndex > 0;
    const hasNext = currentPageIndex < pages.length - 1;
    const currentPage = pages[currentPageIndex];

    const goToPrevious = () => {
        if (hasPrevious) {
            onPageChange(currentPageIndex - 1);
            // Força o scroll para o topo globalmente
            window.scrollTo({ top: 0, behavior: 'auto' });
        }
    };

    const goToNext = () => {
        if (hasNext) {
            onPageChange(currentPageIndex + 1);
            // Força o scroll para o topo globalmente
            window.scrollTo({ top: 0, behavior: 'auto' });
        }
    };

    return (
        <div className={`page-navigator ${darkMode ? 'dark' : ''}`}>
            {/* Indicador de página */}
            <div className="page-indicator">
                <span className="page-current">{currentPageIndex + 1}</span>
                <span className="page-separator">/</span>
                <span className="page-total">{pages.length}</span>
                {currentPage && (
                    <span className="page-title">{currentPage.title || 'Sem título'}</span>
                )}
            </div>

            {/* Controles de navegação */}
            <div className="page-controls">
                <button
                    className="page-btn page-btn-nav"
                    onClick={goToPrevious}
                    disabled={!hasPrevious}
                    title="Página Anterior"
                >
                    <ChevronLeft size={20} />
                </button>

                {/* Pontos indicadores */}
                <div className="page-dots">
                    {pages.map((_, index) => (
                        <button
                            key={index}
                            className={`page-dot ${index === currentPageIndex ? 'active' : ''} ${maxVisibleIndex !== undefined && index > maxVisibleIndex ? 'locked' : ''
                                }`}
                            onClick={() => onPageChange(index)}
                            title={
                                maxVisibleIndex !== undefined && index > maxVisibleIndex
                                    ? 'Página bloqueada — adquira o plano completo'
                                    : `Ir para página ${index + 1}`
                            }
                        />
                    ))}
                </div>

                <button
                    className="page-btn page-btn-nav"
                    onClick={goToNext}
                    disabled={!hasNext || (maxVisibleIndex !== undefined && currentPageIndex >= maxVisibleIndex)}
                    title="Próxima Página"
                >
                    <ChevronRight size={20} />
                </button>

                {/* Botão adicionar (apenas no modo edição) */}
                {isEditing && (
                    <button
                        className="page-btn page-btn-add"
                        onClick={onAddPage}
                        title="Adicionar Nova Página"
                    >
                        <Plus size={20} />
                        <span>Nova Página</span>
                    </button>
                )}
            </div>
        </div>
    );
};

export default PageNavigator;